import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Axios client configured for the HRM API.
 *
 * Auth is **cookie-based** end-to-end:
 *   - Login/signup endpoints set httpOnly `access_token` + `refresh_token` cookies.
 *   - `withCredentials: true` makes the browser attach them to every request.
 *   - No tokens are persisted in localStorage anymore.
 *
 * The Authorization Bearer interceptor is kept as a **fallback** for callers
 * that still set `localStorage.hrm_token` (e.g. older bookmarks during the
 * migration window). It can be removed entirely after one release cycle.
 */
const api = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

api.interceptors.request.use((config) => {
    // FormData uploads must let the browser/axios compute the multipart
    // Content-Type (with the random boundary). Our default JSON header would
    // otherwise short-circuit that → backend rejects the file with
    // "Field required" 422. Strip the header explicitly for FormData bodies.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        if (config.headers && "Content-Type" in config.headers) {
            delete config.headers["Content-Type"];
        }
        if (config.headers && "content-type" in config.headers) {
            delete config.headers["content-type"];
        }
    }
    // Legacy fallback — Bearer token from a pre-cookie login session.
    // Safe to remove once we're confident no users still have a stale token in
    // localStorage (1 deploy cycle should be enough).
    const legacyToken = (typeof localStorage !== "undefined")
        ? localStorage.getItem("hrm_token")
        : null;
    if (legacyToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${legacyToken}`;
    }
    return config;
});

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default api;
